import { useCallback, useEffect, useState } from 'react'
import { Compteur } from '../Compteur'
import { creerCompteLivreur, listerLivreurs, type Identifiants } from '../../api/dispatch'
import type { EtatLivreur, Livreur } from '../../api/types'
import { Ligne, Modale, Panneau } from '../Modale'
import { BlocIdentifiants } from '../BlocIdentifiants'
import { accesARemettre } from '../../api/identifiants'
import { validerContact, type ContactValide } from '../../api/contactCompte'
import { ChampTelephone } from '../../../../../../web-commun/connexion-whatsapp'
import { useTelephone } from '../useTelephone'
import { descriptionNote } from '../../api/notes'
import type { Casquette } from '../../api/discussions'

/**
 * Livreurs — TRANSPOSITION du gabarit d'origine.
 *
 * L'écran précédent était une liste morte : pas de création, pas de fiche, pas
 * de bouton « Message ». Le dispatch ne pouvait donc ni enrôler un livreur ni
 * le joindre depuis sa ligne — les deux gestes les plus fréquents.
 */

/** Les états de la base se regroupent en TROIS familles à l'écran. */
const ETIQUETTE: Record<string, [string, string]> = {
  online: ['En ligne', 'green'],
  available: ['En ligne', 'green'],
  busy: ['En course', 'yellow'],
  delivering: ['En course', 'yellow'],
  offline: ['Hors ligne', 'gray'],
}

type Famille = 'tous' | 'enligne' | 'encourse' | 'horsligne'

const famille = (e: EtatLivreur): Famille =>
  e === 'online' ? 'enligne' : e === 'busy' ? 'encourse' : 'horsligne'

/**
 * Note MIXTE : avis clients réels + note automatique pour chaque livraison que le
 * client n'a pas notée (réglage « Note auto »). Calculée par la base (vue
 * `livreur_notes`, migration 0048) : la console n'a plus de formule à elle.
 * Avant 0048 l'écran affichait « (0 avis clients + 0 auto) » en dur, quelle que
 * soit la réalité.
 */
function noteAffichee(l: Livreur) {
  const { principal, detail } = descriptionNote({ note: l.noteMixte, avis: l.avis, sansAvis: l.sansAvis })
  return (
    <>
      {principal}
      {detail && <span style={{ color: 'var(--n400)', fontSize: 11 }}> {detail}</span>}
    </>
  )
}

export function EcranLivreurs({ surContacter }: { surContacter: (pairId: string, casquette: Casquette) => void }) {
  const [livreurs, setLivreurs] = useState<Livreur[]>([])
  const [filtre, setFiltre] = useState<Famille>('tous')
  const [erreur, setErreur] = useState<string | null>(null)
  const [fiche, setFiche] = useState<Livreur | null>(null)
  const [creation, setCreation] = useState(false)
  const [identifiants, setIdentifiants] = useState<(Identifiants & { nom: string }) | null>(null)

  const charger = useCallback(async () => {
    try {
      setLivreurs(await listerLivreurs())
      setErreur(null)
    } catch (e) {
      setErreur(e instanceof Error ? e.message : 'Lecture impossible.')
    }
  }, [])

  useEffect(() => {
    void charger()
  }, [charger])

  const nb = (f: Famille) =>
    f === 'tous' ? livreurs.length : livreurs.filter((l) => famille(l.etat) === f).length

  const visibles = livreurs.filter((l) => filtre === 'tous' || famille(l.etat) === filtre)

  const FILTRES: Array<[Famille, string]> = [
    ['tous', 'Tous'],
    ['enligne', 'En ligne'],
    ['encourse', 'En course'],
    ['horsligne', 'Hors ligne'],
  ]

  return (
    <>
      <div className="stats">
        <div className="stat">
          <div className="label">Total livreurs</div>
          <div className="val"><Compteur valeur={String(livreurs.length)} /></div>
        </div>
        <div className="stat">
          <div className="label">En ligne</div>
          <div className="val" style={{ color: 'var(--green)' }}>
            <Compteur valeur={String(nb('enligne'))} />
          </div>
        </div>
        <div className="stat">
          <div className="label">En course</div>
          <div className="val" style={{ color: 'var(--warning)' }}>
            <Compteur valeur={String(nb('encourse'))} />
          </div>
        </div>
        <div className="stat">
          <div className="label">Hors ligne</div>
          <div className="val" style={{ color: 'var(--n400)' }}>
            <Compteur valeur={String(nb('horsligne'))} />
          </div>
        </div>
      </div>

      {/* ⚠️ La classe `chip` porte TOUT le style de ces boutons. Sans elle on
          obtient les boutons gris du navigateur — le conteneur `.chip-row` ne
          fait que les aligner. Le compteur `.chip-n` est ce qui évite de
          cliquer sur un filtre pour découvrir qu'il est vide. */}
      <div className="chip-row">
        {FILTRES.map(([cle, libelle]) => (
          <button
            key={cle}
            className={`chip${filtre === cle ? ' active' : ''}`}
            onClick={() => setFiltre(cle)}
          >
            {libelle} <span className="chip-n">{nb(cle)}</span>
          </button>
        ))}
      </div>

      {erreur && <p style={{ color: 'var(--danger)', fontSize: 13 }}>{erreur}</p>}

      <div className="card">
        <div className="card-header">
          Livreurs
          <div className="spacer" style={{ flex: 1 }} />
          <button className="btn btn-accent btn-sm" onClick={() => setCreation(true)}>
            + Nouveau livreur
          </button>
        </div>
        <div className="card-body">
          <table>
            <thead>
              <tr>
                <th>Nom</th>
                <th>Telephone</th>
                <th>Vehicule</th>
                <th>Etat</th>
                <th>Note</th>
                <th>Courses (total)</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {visibles.length === 0 && (
                <tr>
                  <td colSpan={7} style={{ textAlign: 'center', color: 'var(--n400)', padding: 20 }}>
                    Aucun livreur dans cet etat.
                  </td>
                </tr>
              )}
              {visibles.map((l) => {
                const [libelle, couleur] = ETIQUETTE[l.etat] ?? ['Hors ligne', 'gray']
                return (
                  <tr key={l.id}>
                    <td>
                      <a
                        href="#"
                        onClick={(e) => {
                          e.preventDefault()
                          setFiche(l)
                        }}
                      >
                        {l.nom}
                      </a>
                    </td>
                    <td>{l.telephone}</td>
                    <td>
                      {l.typeVehicule} {l.plaque}
                    </td>
                    <td>
                      <span className={`badge badge-${couleur}`}>{libelle}</span>
                    </td>
                    <td>{noteAffichee(l)}</td>
                    {/* ⚠️ ÉCART ASSUMÉ avec l'original, et le seul de cet écran.
                        L'original titrait « Courses auj. » et y mettait
                        `total_deliveries` — le cumul de TOUTE la vie du livreur.
                        Le portage avait choisi l'autre erreur : un 0 écrit en
                        dur. Les deux trompent, en sens inverse.
                        Aucune colonne ne compte les courses du jour ; la seule
                        donnée réelle est le cumul. On l'affiche donc sous son
                        vrai nom. Un dispatcher qui lit « 0 » sur tous ses
                        livreurs cesse de croire l'écran entier. */}
                    <td>{l.livraisons}</td>
                    <td style={{ whiteSpace: 'nowrap' }}>
                      <button className="btn btn-outline btn-sm" onClick={() => setFiche(l)}>
                        Detail
                      </button>{' '}
                      <button className="btn btn-outline btn-sm" onClick={() => surContacter(l.id, 'livreur')}>
                        Message
                      </button>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      </div>

      <Panneau titre={fiche?.nom ?? ''} ouvert={Boolean(fiche)} surFermer={() => setFiche(null)}>
        {fiche && (
          <>
            <Ligne libelle="Nom">{fiche.nom}</Ligne>
            <Ligne libelle="Telephone">{fiche.telephone}</Ligne>
            <Ligne libelle="Vehicule">
              {fiche.typeVehicule} — {fiche.plaque} ({fiche.modele})
            </Ligne>
            <Ligne libelle="Statut">
              <span className={`badge badge-${(ETIQUETTE[fiche.etat] ?? ['', 'gray'])[1]}`}>
                {(ETIQUETTE[fiche.etat] ?? ['Hors ligne'])[0]}
              </span>
            </Ligne>
            <Ligne libelle="Note (clients + auto)">{noteAffichee(fiche)}</Ligne>
            <Ligne libelle="Courses livrées (total)">{fiche.livraisons}</Ligne>
            <Ligne libelle="Paiement">
              {fiche.fournisseurPaie} — {fiche.numeroPaie}
            </Ligne>
          </>
        )}
      </Panneau>

      {creation && (
        <ModaleLivreur
          surFermer={() => setCreation(false)}
          surCreer={async (v) => {
            const cred = await creerCompteLivreur(v)
            setCreation(false)
            setIdentifiants({ email: v.contact.email, phone: v.contact.phone, ...cred, nom: v.nom })
            await charger()
          }}
        />
      )}

      {identifiants && (
        <Modale titre="Compte livreur cree" ouverte surFermer={() => setIdentifiants(null)}>
          <div style={{ fontSize: 13, lineHeight: 1.6 }}>
            <b>{identifiants.nom}</b> est cree.{' '}
            <BlocIdentifiants
              acces={accesARemettre(identifiants)}
              introduction="Transmettez ces accès au livreur — il se connecte à l'app Livreur puis change son mot de passe :"
            />
            <button className="btn btn-accent" onClick={() => setIdentifiants(null)}>
              Termine
            </button>
          </div>
        </Modale>
      )}
    </>
  )
}

function ModaleLivreur({
  surFermer,
  surCreer,
}: {
  surFermer: () => void
  surCreer: (v: {
    nom: string
    contact: ContactValide
    typeVehicule: string
    plaque: string
    modele: string
    fournisseurPaiement: string
    numeroPaiement: string
  }) => Promise<void>
}) {
  const [prenom, setPrenom] = useState('')
  const [nom, setNom] = useState('')
  const [email, setEmail] = useState('')
  const [telephone, setTelephone] = useTelephone()
  const [champContact, setChampContact] = useState<'email' | 'telephone' | 'contact' | null>(null)
  const [typeVehicule, setTypeVehicule] = useState('moto')
  const [plaque, setPlaque] = useState('')
  const [modele, setModele] = useState('')
  const [fournisseur, setFournisseur] = useState('wave')
  const [numero, setNumero] = useState('')
  const [erreur, setErreur] = useState<string | null>(null)
  const [envoi, setEnvoi] = useState(false)

  async function creer() {
    if (!prenom.trim() || !nom.trim()) {
      setErreur('Prenom et nom requis.')
      return
    }
    /* Au moins un e-mail OU un numéro WhatsApp ; chacun, s'il est renseigné, doit être valide. */
    const contact = validerContact({ email, telephone })
    if (!contact.ok) {
      setErreur(contact.message)
      setChampContact(contact.champ)
      return
    }
    setChampContact(null)
    setEnvoi(true)
    setErreur(null)
    try {
      await surCreer({
        nom: `${prenom.trim()} ${nom.trim()}`,
        contact: contact.contact,
        typeVehicule,
        plaque,
        modele,
        fournisseurPaiement: fournisseur,
        numeroPaiement: numero,
      })
    } catch (e) {
      setErreur(e instanceof Error ? e.message : 'Creation impossible.')
    } finally {
      setEnvoi(false)
    }
  }

  return (
    <Modale titre="Nouveau livreur" ouverte surFermer={surFermer}>
      <div style={{ display: 'flex', gap: 10 }}>
        <div style={{ flex: 1 }}>
          <label>Prenom</label>
          <input value={prenom} onChange={(e) => setPrenom(e.target.value)} autoFocus />
        </div>
        <div style={{ flex: 1 }}>
          <label>Nom</label>
          <input value={nom} onChange={(e) => setNom(e.target.value)} />
        </div>
      </div>

      <label>E-mail (recommandé)</label>
      <input
        type="email"
        placeholder="livreur@exemple.ml"
        value={email}
        aria-invalid={champContact === 'email' || champContact === 'contact' ? true : undefined}
        onChange={(e) => setEmail(e.target.value)}
      />

      <ChampTelephone
        valeur={telephone}
        surChangement={setTelephone}
        montrerErreur={champContact === 'telephone'}
        aide="Au Mali, c'est souvent le plus pratique : le livreur reçoit un code sur WhatsApp à chaque connexion."
      />
      <p style={{ fontSize: 11.5, color: 'var(--n400)', margin: '4px 0 0' }}>
        Renseignez au moins l'un des deux : l'e-mail ou le numéro WhatsApp.
      </p>

      <label>Type vehicule</label>
      <select value={typeVehicule} onChange={(e) => setTypeVehicule(e.target.value)}>
        <option>moto</option>
        <option>velo</option>
        <option>voiture</option>
      </select>

      <label>Plaque</label>
      <input value={plaque} onChange={(e) => setPlaque(e.target.value)} />
      <label>Modele</label>
      <input value={modele} onChange={(e) => setModele(e.target.value)} />

      <label>Provider paiement</label>
      <select value={fournisseur} onChange={(e) => setFournisseur(e.target.value)}>
        <option value="wave">wave</option>
        <option value="om">om</option>
      </select>

      <label>Numero paiement</label>
      <input value={numero} onChange={(e) => setNumero(e.target.value)} />

      <div
        style={{
          fontSize: 11.5,
          color: 'var(--n700)',
          background: 'var(--accent-light)',
          borderRadius: 6,
          padding: '8px 10px',
          marginTop: 8,
        }}
      >
        Un <b>compte livreur</b> sera créé. Avec un <b>e-mail</b>, il se connecte à l'app Livreur avec un
        code temporaire (qu'il changera dans l'app) ; avec un <b>numéro WhatsApp</b> seul, il reçoit un
        code sur WhatsApp à chaque connexion.
      </div>

      {erreur && <p style={{ color: 'var(--danger)', fontSize: 12.5, marginTop: 8 }}>{erreur}</p>}

      <button
        className="btn btn-accent"
        style={{ marginTop: 16 }}
        disabled={envoi}
        onClick={() => void creer()}
      >
        {envoi ? 'Creation…' : 'Creer le livreur + le compte'}
      </button>
    </Modale>
  )
}
